import React, { useState } from 'react'

import clsx from 'clsx'
import dynamic from 'next/dynamic'

import LogoGANegativeSVG from 'assets/LogoGANegativeSVG.webp'
import { IconButton } from 'components/Button'
import Grid, { Container } from 'components/Grid'
import Icon from 'components/Icon'
import CompareBasketMobile from 'components/Layout/components/CompareBasketMobile'
import HeaderCompareBasket from 'components/Layout/components/HeaderCompareBasket'
import SiteMenus from 'components/SiteMenus'
import { useAppContext } from 'context/AppContext'
import { useCompareBasket } from 'context/CompareBasketContext'
import { useEventContext } from 'context/EventContext'
import { yearsSinceGetapp } from 'helpers/brandDates'
import useScrollTrigger from 'hooks/useScrollTrigger'
import useUser from 'hooks/useUser'
import useViewport from 'hooks/useViewport'

import classes from './Appbar.module.scss'

const UserDrawer = dynamic(() => import('components/UserDrawer'))
const Search = dynamic(() => import('components/Search'))

const AppBar = () => {
  const [hasMobileSearch, setHasMobileSearch] = useState(false)
  const { addEvent } = useEventContext()
  const isSticked = useScrollTrigger({ threshold: 0 })
  const { down, is } = useViewport()
  const [user] = useUser()
  const compareBasket = useCompareBasket()
  const { isCypressTakingSnapshots } = useAppContext()

  const isSmallDevice = down('sm')
  const isMediumDevice = down('md')
  const showDesktopSearch = !isSmallDevice && isSticked
  const showLogo = is('xs') ? !hasMobileSearch : true

  let showProductSlogan = false
  if (is('sm')) {
    showProductSlogan = !hasMobileSearch
  } else if (!is('xs')) {
    showProductSlogan = showLogo && !isSticked
  }

  return (
    <>
      {isSmallDevice && <CompareBasketMobile />}
      <header
        data-testid="app-bar"
        className={clsx('AppBar', classes.root, {
          [classes.withSticky]: !isCypressTakingSnapshots,
          [classes.isSticked]: isSticked,
        })}
      >
        <Container
          component={Grid}
          container
          className={clsx(classes.content, hasMobileSearch && classes.contentWithSearch)}
        >
          <Grid item xs={showLogo ? 8 : 2} sm={showProductSlogan ? 11 : 5} md={8} lg={7} className={classes.menuLeft}>
            <div className={classes.menuLeftItems}>
              {isMediumDevice && <UserDrawer user={user} className={classes.burgerMenu} />}
              {showLogo && (
                <div className={clsx(classes.logoContainer, !showDesktopSearch && !isMediumDevice && classes.grow)}>
                  <a
                    href="/"
                    aria-label="getapp-logo-link"
                    {...addEvent({
                      engagementComponent: 'header',
                      engagementDestination: 'go-to_home',
                      engagementDetails: 'logo_getapp',
                      name: 'engagement_navigation',
                    })}
                  >
                    <img src={LogoGANegativeSVG.src} alt="getapp-logo" className={classes.logo} />
                    {showProductSlogan && (
                      <span className={classes.logoSlogan}>{yearsSinceGetapp}+ Years Recommending Software</span>
                    )}
                  </a>
                </div>
              )}
            </div>
            {showDesktopSearch && (
              <div className={classes.searchContainer}>
                <Search colorVariant="light" />
              </div>
            )}
            {!isSmallDevice && !!compareBasket.items.length && (
              <div className={classes.basketContainer}>
                <HeaderCompareBasket variant="light" />
              </div>
            )}
          </Grid>
          <Grid item xs={showLogo ? 4 : 10} sm={showProductSlogan ? 1 : 7} md={4} lg={5} className={classes.menuRight}>
            <SiteMenus user={user} colorVariant="light" />
            {isSmallDevice &&
              (hasMobileSearch ? (
                <div className={clsx(classes.searchContainer, classes.mobileSearch)}>
                  <Search colorVariant="light" focusOnDisplay onBlur={() => setHasMobileSearch(false)} />
                </div>
              ) : (
                <IconButton
                  data-testid="search-icon"
                  size="small"
                  onClick={() => setHasMobileSearch(true)}
                  className={classes.searchIconButton}
                >
                  <Icon icon={['fas', 'search']} size="24" className={classes.searchIcon} />
                </IconButton>
              ))}
          </Grid>
        </Container>
      </header>
    </>
  )
}

export default AppBar
